import { useMemo, useState } from 'react';
import { BarChart } from '../../charts/BarChart';
import { BarList, ChartCard, Stat } from '../../components/data';
import { ErrorBanner, Field, Loading } from '../../components/ui';
import { downloadCsv, fileStamp } from '../../lib/download';
import { formatNumber, num, periodLabel, toLocalDateString, todayLocal } from '../../lib/format';
import { useHeldResource } from '../../lib/hooks';

interface SalesOrder {
  id: number;
  title: string;
  status: string;
  date: string | null;
  promised: string | null;
  company: string | null;
  owner: string | null;
  opportunity: string | null;
  hasFigures: boolean;
  revenue: number | null;
  cost: number | null;
  margin: number | null;
  marginPct: number | null;
  onetimeRevenue: number | null;
  onetimeCost: number | null;
  monthlyRevenue: number | null;
  monthlyCost: number | null;
  months: number | null;
}
interface Payload {
  from: string;
  to: string;
  orders: SalesOrder[];
  fetchedUtc: string;
}

const money = (v: number | null) => (v === null ? '—' : formatNumber(v, 'currency'));
const pct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);
const day = (iso: string | null) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

/* The quick ranges; Custom is the From / To boxes. */
function quickRange(k: 'month' | 'lastMonth' | 'quarter' | 'ytd' | '12m'): { from: string; to: string } {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth();
  if (k === 'month') return { from: toLocalDateString(new Date(y, m, 1)), to: todayLocal() };
  if (k === 'lastMonth') return { from: toLocalDateString(new Date(y, m - 1, 1)), to: toLocalDateString(new Date(y, m, 0)) };
  if (k === 'quarter') return { from: toLocalDateString(new Date(y, m - (m % 3), 1)), to: todayLocal() };
  if (k === 'ytd') return { from: `${y}-01-01`, to: todayLocal() };
  const s = new Date(y, m - 11, 1);
  return { from: toLocalDateString(s), to: todayLocal() };
}
const QUICK: { k: Parameters<typeof quickRange>[0]; label: string }[] = [
  { k: 'month', label: 'This month' },
  { k: 'lastMonth', label: 'Last month' },
  { k: 'quarter', label: 'This quarter' },
  { k: 'ytd', label: 'This year' },
  { k: '12m', label: 'Last 12 months' },
];

/**
 * Admin › Sales orders: Autotask's sales orders for the dates chosen, with the
 * margin each carries (revenue and cost from the opportunity the order came
 * from — routes/adminSalesOrders.ts). Totals, margin by month, by owner and
 * by client, and every order. Read once per range, kept until Refresh.
 */
export function AdminSalesOrders() {
  const [{ from, to }, setRange] = useState(() => quickRange('ytd'));
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [status, setStatus] = useState('');
  const [owner, setOwner] = useState('');
  const [search, setSearch] = useState('');
  const data = useHeldResource<Payload>(`/admin/sales-orders?${new URLSearchParams({ from, to })}`);
  const all = data.data?.orders ?? [];

  const apply = (r: { from: string; to: string }) => {
    setDraftFrom(r.from);
    setDraftTo(r.to);
    setRange(r);
  };
  const draftOk = Boolean(draftFrom && draftTo && draftFrom <= draftTo);

  const statuses = useMemo(() => [...new Set(all.map((o) => o.status))].sort(), [all]);
  const owners = useMemo(() => [...new Set(all.map((o) => o.owner ?? 'No owner'))].sort(), [all]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter(
      (o) =>
        (!status || o.status === status) &&
        (!owner || (o.owner ?? 'No owner') === owner) &&
        (!q || [o.title, o.company, o.opportunity, o.owner].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))),
    );
  }, [all, status, owner, search]);

  const priced = rows.filter((o) => o.hasFigures);
  const sum = (k: 'revenue' | 'cost' | 'margin' | 'onetimeRevenue' | 'onetimeCost' | 'monthlyRevenue' | 'monthlyCost') =>
    priced.reduce((t, o) => t + (o[k] ?? 0), 0);
  const revenue = sum('revenue');
  const cost = sum('cost');
  const margin = revenue - cost;
  const marginPct = revenue ? (margin / revenue) * 100 : null;
  const monthlyMargin = sum('monthlyRevenue') - sum('monthlyCost');
  const onetimeMargin = sum('onetimeRevenue') - sum('onetimeCost');

  /* Revenue, cost and margin per month of the sales order date. */
  const byMonth = useMemo(() => {
    const keys: string[] = [];
    const d = new Date(`${from}T00:00:00`);
    d.setDate(1);
    const end = new Date(`${to}T00:00:00`);
    while (d <= end) {
      keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      d.setMonth(d.getMonth() + 1);
    }
    const at = new Map(keys.map((k) => [k, { month: k, revenue: 0, cost: 0, margin: 0 }]));
    for (const o of priced) {
      const r = o.date ? at.get(o.date.slice(0, 7)) : undefined;
      if (!r) continue;
      r.revenue += o.revenue ?? 0;
      r.cost += o.cost ?? 0;
      r.margin += o.margin ?? 0;
    }
    return [...at.values()];
  }, [priced, from, to]);

  const group = (key: (o: SalesOrder) => string) => {
    const m = new Map<string, { name: string; revenue: number; margin: number; orders: number }>();
    for (const o of priced) {
      const k = key(o);
      const g = m.get(k) ?? { name: k, revenue: 0, margin: 0, orders: 0 };
      g.revenue += o.revenue ?? 0;
      g.margin += o.margin ?? 0;
      g.orders += 1;
      m.set(k, g);
    }
    return [...m.values()].sort((a, b) => b.margin - a.margin);
  };
  const byOwner = group((o) => o.owner ?? 'No owner');
  const byClient = group((o) => o.company ?? 'No client').slice(0, 10);

  const exportCsv = () =>
    downloadCsv(`sales_orders_${from}_to_${to}_${fileStamp()}.csv`, [
      ['Date', 'Sales order', 'Client', 'Owner', 'Status', 'Opportunity', 'Revenue', 'Cost', 'Margin', 'Margin %', 'One-time revenue', 'One-time cost', 'Monthly revenue', 'Monthly cost'],
      ...rows.map((o) => [o.date, o.title, o.company, o.owner, o.status, o.opportunity, o.revenue, o.cost, o.margin, o.marginPct, o.onetimeRevenue, o.onetimeCost, o.monthlyRevenue, o.monthlyCost]),
    ]);

  return (
    <div className="mt-4">
      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Sales orders dated from">
              <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <input className="input" type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
            </Field>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!draftOk || (draftFrom === from && draftTo === to)}
              onClick={() => setRange({ from: draftFrom, to: draftTo })}
            >
              Show
            </button>
            <div className="row wrap" style={{ gap: 'var(--space-1)' }}>
              {QUICK.map((q) => (
                <button key={q.k} type="button" className="btn btn-ghost btn-sm" onClick={() => apply(quickRange(q.k))}>
                  {q.label}
                </button>
              ))}
            </div>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>
          <p className="xs muted mt-2">
            Autotask's sales orders dated {day(from)} – {day(to)}. Revenue and cost are the totals on the opportunity each order came from;
            margin is revenue less cost.
            {data.fetchedAt &&
              ` As of ${new Date(data.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`}
          </p>
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && !data.data && <Loading label="Reading sales orders from Autotask" />}

      {data.data && (
        <>
          <div className="grid grid-4 mb-4">
            <Stat label="Sales orders" value={num(rows.length)} foot={priced.length < rows.length ? `${rows.length - priced.length} with no opportunity figures` : 'all with figures'} />
            <Stat label="Revenue" value={money(revenue)} foot={`cost ${money(cost)}`} />
            <Stat label="Margin" value={money(margin)} flagged={margin < 0} foot={`one-time ${money(onetimeMargin)} · monthly ${money(monthlyMargin)}`} />
            <Stat label="Margin %" value={marginPct === null ? '—' : `${marginPct.toFixed(1)}%`} flagged={marginPct !== null && marginPct < 0} foot="margin over revenue" />
          </div>

          <ChartCard className="mb-4" title="Revenue, cost and margin by month" subtitle="By sales order date.">
            <BarChart
              data={byMonth}
              xKey="month"
              series={[
                { key: 'revenue', label: 'Revenue', color: 'var(--series-1)' },
                { key: 'cost', label: 'Cost', color: 'var(--series-3)' },
                { key: 'margin', label: 'Margin', color: 'var(--series-2)' },
              ]}
              xFormat={periodLabel}
              yFormat={(v) => formatNumber(v, 'currency')}
              height={240}
              emptyMessage="No sales orders in this range."
            />
          </ChartCard>

          <div className="grid grid-2 mb-4">
            <ChartCard title="Margin by owner" subtitle="Click one to filter the list.">
              <div className="pa-pick">
                {byOwner.map((g) => (
                  <button key={g.name} type="button" className={owner === g.name ? 'is-on' : undefined} onClick={() => setOwner(owner === g.name ? '' : g.name)}>
                    <BarList rows={[{ name: g.name, value: Math.max(0, g.margin), display: `${money(g.margin)} · ${g.revenue ? ((g.margin / g.revenue) * 100).toFixed(1) : '—'}% · ${g.orders}` }]} />
                  </button>
                ))}
              </div>
            </ChartCard>
            <ChartCard title="Top clients by margin" subtitle="The ten largest.">
              <BarList
                rows={byClient.map((g) => ({
                  name: g.name,
                  value: Math.max(0, g.margin),
                  display: `${money(g.margin)} · ${g.revenue ? ((g.margin / g.revenue) * 100).toFixed(1) : '—'}%`,
                }))}
              />
            </ChartCard>
          </div>

          <section className="card">
            <div className="card-head">
              <div className="grow">
                <h3>
                  {num(rows.length)} sales order{rows.length === 1 ? '' : 's'}
                  {[status, owner].filter(Boolean).length ? ` — ${[status, owner].filter(Boolean).join(' · ')}` : ''}
                </h3>
                <p className="sub">Newest first. Negative margins are in red.</p>
              </div>
              <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-2)' }}>
                <Field label="Search">
                  <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Order, client, opportunity" />
                </Field>
                <Field label="Status">
                  <select className="select" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">All</option>
                    {statuses.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Owner">
                  <select className="select" value={owner} onChange={(e) => setOwner(e.target.value)}>
                    <option value="">Everyone</option>
                    {owners.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <button type="button" className="btn btn-secondary btn-sm" disabled={rows.length === 0} onClick={exportCsv}>
                  Export CSV
                </button>
              </div>
            </div>
            {rows.length === 0 ? (
              <div className="card-body">
                <p className="muted small">No sales orders match.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Sales order</th>
                      <th>Client</th>
                      <th>Owner</th>
                      <th>Status</th>
                      <th className="num">Revenue</th>
                      <th className="num">Cost</th>
                      <th className="num">Margin</th>
                      <th className="num">Margin %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((o) => {
                      const neg = (o.margin ?? 0) < 0;
                      return (
                        <tr key={o.id}>
                          <td className="xs" style={{ whiteSpace: 'nowrap' }}>{day(o.date)}</td>
                          <td>
                            {o.title || '(untitled)'}
                            {o.opportunity && o.opportunity !== o.title && <div className="xs muted">{o.opportunity}</div>}
                          </td>
                          <td>{o.company ?? '—'}</td>
                          <td>{o.owner ?? <span className="subtle">No owner</span>}</td>
                          <td>{o.status}</td>
                          <td className="num">{money(o.revenue)}</td>
                          <td className="num">{money(o.cost)}</td>
                          <td className="num strong" style={neg ? { color: 'var(--status-danger)' } : undefined}>{money(o.margin)}</td>
                          <td className="num" style={neg ? { color: 'var(--status-danger)' } : undefined}>{pct(o.marginPct)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
